import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDuration } from "@/lib/time";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Gauge, Zap, Timer, Bell, AlertTriangle, Award, TrendingUp } from "lucide-react";

function Stat({ label, value, accent, icon: Icon, testId }) {
  return (
    <div className="bg-card border border-border p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="font-display text-4xl font-extrabold mt-2 leading-none" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

const PERIODS = [["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["all", "All time"]];

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export default function Coaching() {
  const { user } = useAuth();
  const isManager = ["supervisor", "company_admin", "platform_admin"].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(isManager ? "" : user?.id);
  const [period, setPeriod] = useState("30");
  const [card, setCard] = useState(null);

  useEffect(() => {
    if (isManager) api.get("/users").then((r) => {
      const agents = r.data.users.filter((u) => ["warehouse_agent", "front_desk_agent", "supervisor"].includes(u.role));
      setEmployees(agents);
      if (!selected && agents.length) setSelected(agents[0].id);
    }).catch(() => {});
  }, [isManager]); // eslint-disable-line

  useEffect(() => {
    const params = new URLSearchParams();
    if (isManager && selected) params.set("user_id", selected);
    if (period !== "all") params.set("date_from", daysAgoIso(Number(period)));
    api.get(`/reports/scorecard?${params.toString()}`).then((r) => setCard(r.data)).catch(() => {});
  }, [selected, period, isManager]);

  return (
    <DashboardLayout title="Coaching" needsStore={false}>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {isManager && (
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-64" data-testid="coaching-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} — {e.role.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44" data-testid="coaching-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {card && (
        <>
          <div className="bg-card border border-border p-6 mb-6 flex items-center justify-between flex-wrap gap-4" data-testid="coaching-header">
            <div>
              <div className="font-display text-4xl font-extrabold tracking-tight">{card.full_name}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wide">{card.role?.replace(/_/g, " ")} · {card.employee_number || "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">On-Target Rate</div>
              <div className="font-display text-6xl font-extrabold leading-none"
                style={{ color: card.within_pct >= 80 ? "#22C55E" : card.within_pct >= 50 ? "#F59E0B" : "#FF3B30" }}>
                {card.within_pct}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <Stat label="Handled" value={card.handled} accent="#fff" icon={Gauge} testId="coaching-handled" />
            <Stat label="Avg Time" value={fmtDuration(card.avg_seconds)} accent="#fff" icon={Timer} />
            <Stat label="Fastest" value={fmtDuration(card.fastest_seconds)} accent="#22C55E" icon={Zap} />
            <Stat label="Slowest" value={fmtDuration(card.slowest_seconds)} accent="#F59E0B" icon={TrendingUp} />
            <Stat label="On Target" value={card.within_target} accent="#22C55E" icon={Award} />
            <Stat label="Missed" value={card.exceeded_target} accent="#FF3B30" icon={AlertTriangle} />
            <Stat label="Alerts (Ack)" value={`${card.alerts_acked}/${card.alerts}`} accent="#F59E0B" icon={Bell} />
          </div>

          <div className="bg-card border border-border p-5" data-testid="coaching-chart">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Transactions Handled — Last 7 Days</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={card.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="day" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", color: "#fff" }} />
                <Bar dataKey="count" fill="#22C55E" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
